import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { Box } from '@mui/material';
import Tab from '@mui/material/Tab';
import { GetLightInvoicesResponse } from '@vy/api-definition';
import { INTEGRATION_PROVIDERS, IntegrationProviders, SystemEvent } from '@vy/types';
import { isNotNullOrUndefined, ItemOf } from '@vy/utils';
import { useState } from 'react';
import { VyTabList } from '../../../../../../../components/new-design/navigation/VyTabList';
import {
  prefetchNetSuiteClasses,
  prefetchNetSuiteProducts,
  useMorningEnabled,
  useXeroEnabled,
  prefetchXeroProducts,
  useQuickbooksEnabled,
} from '../../../../../../../hooks/requests/integrations';
import { useIntegrationPrefetch } from '../../../../../../../hooks/requests/integrations/use-conditional-integration-prefetch';
import { useActivatedIntegration } from '../../../../../../../hooks/use-activated-integration';
import { InvoiceActivityXeroSyncTab } from './invoice-activity-tabs/InvoiceActivityXeroSyncTab/InvoiceActivityXeroSyncTab';
import { InvoiceActivityPaddleSyncTab } from './invoice-activity-tabs/InvoiceActivityPaddleSyncTab/InvoiceActivityPaddleSyncTab';
import { useExportedInvoiceIntegration } from '../../../../../../../hooks/requests/integrations/use-exported-invoice-integration';
import { useConnectedIntegrations } from '../../../../../../../hooks/requests/use-integrations';
import { TAB_OPTIONS } from './constants/tab-options-constants';
import { InvoiceActivityNetSuiteSyncTab } from './invoice-activity-tabs/InvoiceActivityErpSyncTab/InvoiceActivityNetSuiteSyncTab';
import { InvoiceActivityMorningSyncTab } from './invoice-activity-tabs/InvoiceActivityMorningSyncTab/InvoiceActivityMorningSyncTab';
import { InvoiceActivityQbSyncTab } from './invoice-activity-tabs/InvoiceActivityQbSyncTab/InvoiceActivityQbSyncTab';
import { InvoiceTab } from './invoice-activity-tabs/InvoiceTab';

type InvoiceActivityTabsProps = {
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>;
  systemEvents: SystemEvent[];
};

export const InvoiceActivityTabs: React.FC<InvoiceActivityTabsProps> = ({ invoice, systemEvents }) => {
  const [selectedTab, setSelectedTab] = useState(TAB_OPTIONS.Invoice);
  const { data: integrationProviders } = useConnectedIntegrations();
  const { data: exportedInvoiceIntegrations = [] } = useExportedInvoiceIntegration(invoice.id);

  const isNetSuiteConnected = isNetSuiteIntegrationConnected(integrationProviders);
  const isQuickBooksEnabled = useQuickbooksEnabled();
  const isMorningEnabled = useMorningEnabled();
  const isXeroEnabled = useXeroEnabled();
  const isPaddleEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.paddle);

  if (isNetSuiteConnected) {
    useIntegrationPrefetch(
      INTEGRATION_PROVIDERS.netSuite,
      (qc) => prefetchNetSuiteProducts(qc, invoice.customer.id),
      [invoice.customer.id],
    );
    useIntegrationPrefetch(INTEGRATION_PROVIDERS.netSuite, prefetchNetSuiteClasses);
  }

  if (isXeroEnabled) {
    useIntegrationPrefetch(INTEGRATION_PROVIDERS.xero, prefetchXeroProducts);
  }

  const handleChange = (_event: React.SyntheticEvent, newTab: string) => {
    setSelectedTab(newTab);
  };

  return (
    <TabContext value={selectedTab}>
      <Box sx={{ width: '100%', pl: 2, pr: 2, pt: 2 }}>
        <VyTabList
          onChange={handleChange}
          variant="fullWidth"
          slotProps={{
            indicator: {
              sx: {
                height: '2px',
              },
            },
          }}
        >
          <Tab label="Invoice" value={TAB_OPTIONS.Invoice} />
          {isNetSuiteConnected && <Tab label="NS sync" value={TAB_OPTIONS.ErpSync} />}
          {isQuickBooksEnabled && <Tab label="QB sync" value={TAB_OPTIONS.QbSync} />}
          {isMorningEnabled && <Tab label="Morning sync" value={TAB_OPTIONS.MorningSync} />}
          {isXeroEnabled && <Tab label="Xero sync" value={TAB_OPTIONS.XeroSync} />}
          {isPaddleEnabled && <Tab label="Paddle sync" value={TAB_OPTIONS.PaddleSync} />}
        </VyTabList>
      </Box>

      <TabPanel value={TAB_OPTIONS.Invoice}>
        <InvoiceTab
          invoice={invoice}
          systemEvents={systemEvents}
          exportedInvoiceIntegrations={exportedInvoiceIntegrations}
        />
      </TabPanel>

      {isNetSuiteConnected && (
        <TabPanel value={TAB_OPTIONS.ErpSync}>
          <InvoiceActivityNetSuiteSyncTab
            invoice={invoice}
            exportedInvoiceIntegrations={exportedInvoiceIntegrations}
          />
        </TabPanel>
      )}

      {isQuickBooksEnabled && (
        <TabPanel value={TAB_OPTIONS.QbSync}>
          <InvoiceActivityQbSyncTab
            invoice={invoice}
            exportedInvoiceIntegrations={exportedInvoiceIntegrations}
          />
        </TabPanel>
      )}

      {isMorningEnabled && (
        <TabPanel value={TAB_OPTIONS.MorningSync}>
          <InvoiceActivityMorningSyncTab
            invoice={invoice}
            exportedInvoiceIntegrations={exportedInvoiceIntegrations}
          />
        </TabPanel>
      )}

      {isXeroEnabled && (
        <TabPanel value={TAB_OPTIONS.XeroSync}>
          <InvoiceActivityXeroSyncTab
            invoice={invoice}
            exportedInvoiceIntegrations={exportedInvoiceIntegrations}
          />
        </TabPanel>
      )}

      {isPaddleEnabled && (
        <TabPanel value={TAB_OPTIONS.PaddleSync}>
          <InvoiceActivityPaddleSyncTab
            invoice={invoice}
            exportedInvoiceIntegrations={exportedInvoiceIntegrations}
          />
        </TabPanel>
      )}
    </TabContext>
  );
};

function isNetSuiteIntegrationConnected(integrationProviders?: IntegrationProviders[]) {
  return (
    isNotNullOrUndefined(integrationProviders) &&
    integrationProviders.includes(INTEGRATION_PROVIDERS.netSuite)
  );
}
